import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import apiClient from '@/lib/api/apiClient';
import {
  BillingCatalogResponse,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateCustomerPortalSessionResponse,
  GetCurrentSubscriptionResponse,
} from '@inversor-hub/types';
import { ME_QUERY_KEY } from '@/lib/api/me.ts';

export const BILLING_SUBSCRIPTION_QUERY_KEY = 'billing-subscription';
export const BILLING_CATALOG_QUERY_KEY = 'billing-catalog';

export function useGetBillingSubscription(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [BILLING_SUBSCRIPTION_QUERY_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<GetCurrentSubscriptionResponse>('/billing/subscription');
      return data.data;
    },
    enabled: isAuthenticated && (options?.enabled ?? true),
    refetchOnWindowFocus: true,
  });
}

export function useGetBillingCatalog(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [BILLING_CATALOG_QUERY_KEY],
    queryFn: async () => {
      const { data } = await apiClient.get<BillingCatalogResponse>('/billing/catalog');
      return data.data;
    },
    enabled: isAuthenticated && (options?.enabled ?? true),
  });
}

export function useCreateBillingCheckoutSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCheckoutSessionRequest) => {
      const { data } = await apiClient.post<CreateCheckoutSessionResponse>(
        '/billing/checkout-session',
        input
      );
      return data.data.url;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [BILLING_SUBSCRIPTION_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] });
    },
  });
}

export function useCreateBillingPortalSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<CreateCustomerPortalSessionResponse>(
        '/billing/customer-portal-session'
      );
      return data.data.url;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [BILLING_SUBSCRIPTION_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] }),
      ]);
    },
  });
}
